import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Ad } from '@/pages/AdminAdsPage';
import { isStagingModeEnabled } from '@/utils/stagingMode';

export function useActiveAds(displayMode: string, targetSection?: string): Ad[] {
  const location = useLocation();
  const [activeAds, setActiveAds] = useState<Ad[]>([]);
  const [hasClicked, setHasClicked] = useState(false);

  // Detect global first click
  useEffect(() => {
    const handleFirstClick = () => {
      setHasClicked(true);
      sessionStorage.setItem('kayninHasClicked', 'true');
      document.removeEventListener('click', handleFirstClick);
    };
    if (sessionStorage.getItem('kayninHasClicked')) {
      setHasClicked(true);
    } else {
      document.addEventListener('click', handleFirstClick);
    }
    return () => document.removeEventListener('click', handleFirstClick);
  }, []);

  useEffect(() => {
    const evaluateAds = () => {
      const saved = localStorage.getItem("kayninAds");
      if (!saved) {
        setActiveAds([]);
        return;
      }
      
      const allAds: Ad[] = JSON.parse(saved);
      const isStaging = isStagingModeEnabled();
      let modeAds = allAds.filter(a => a.active && a.displayMode === displayMode);
      if (!isStaging) {
        modeAds = modeAds.filter(a => a.publishStatus !== 'draft');
      }
      if (targetSection) {
        modeAds = modeAds.filter(a => a.targetSection === targetSection);
      }

      // Filtrage par date de programmation
      const now = new Date().getTime();
      modeAds = modeAds.filter(a => {
        if (a.startDate && new Date(a.startDate).getTime() > now) return false;
        if (a.endDate && new Date(a.endDate).getTime() < now) return false;
        return true;
      });
      
      const validAds = modeAds.filter(ad => {
        const trigger = ad.popupTrigger || 'homepage';
        
        if (trigger === 'homepage') return location.pathname === '/';
        if (trigger === 'first_click') return hasClicked;
        if (trigger === 'always') return true;
        if (trigger === 'specific_page' && ad.targetPage) return location.pathname === ad.targetPage;
        
        return false;
      });

      setActiveAds(validAds);
    };

    evaluateAds();

    const handleUpdate = () => evaluateAds();
    window.addEventListener("kayninAdsUpdated", handleUpdate);
    window.addEventListener("stagingModeChanged", handleUpdate);
    return () => {
      window.removeEventListener("kayninAdsUpdated", handleUpdate);
      window.removeEventListener("stagingModeChanged", handleUpdate);
    };
  }, [location.pathname, hasClicked, displayMode, targetSection]);

  return activeAds;
}
